import { DefaultButton } from '@fluentui/react/lib/Button';
import { ContextualMenuItemType, IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { ITextField, TextField } from '@fluentui/react/lib/TextField';
import { observer } from 'mobx-react-lite';
import { ControlIcons } from 'owa-control-icons';
import loc from 'owa-localize';
import * as React from 'react';
import {
    titleConfirmTooltip,
    titleEditTooltip,
    titleTextFieldLabel,
    calendarAssetName,
} from '../calendar.locstring.json';
import type { CalendarTitleProps } from '../schema';
import * as styles from './styles.scss';
import { KeyCodes } from '@fluentui/react/lib/Utilities';

const editButtonIconProps = {
    iconName: ControlIcons.Edit,
    styles: { root: styles.calendarTitleIcon },
};

const confirmButtonIconProps = {
    iconName: ControlIcons.CheckMark,
    styles: { root: styles.calendarTitleIcon },
};

const textFieldStyles = {
    field: styles.calendarTitleTextBoxField,
    fieldGroup: styles.calendarTitleTextBoxFieldGroup,
};

export const CalendarTitle = observer(function CalendarTitle(props: CalendarTitleProps) {
    const { title, setTitle } = props;
    const [isEditing, setIsEditing] = React.useState<boolean>(false);
    const titleTextFieldRef = React.useRef<ITextField | null>(null);
    const [titleInputValue, setTitleInputValue] = React.useState<string | undefined>(title);

    const onStartEditTitle = React.useCallback(() => {
        setIsEditing(true);
        if (titleTextFieldRef.current) {
            titleTextFieldRef.current.focus();
        }
    }, []);

    const onChangeTitle = React.useCallback(
        (_event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
            setTitleInputValue(newValue);
            onStartEditTitle();
        },
        [onStartEditTitle]
    );

    const onEndEditingTitle = React.useCallback(() => {
        setIsEditing(false);
    }, []);

    const onConfirmTitle = React.useCallback(() => {
        onEndEditingTitle();
        setTitle(titleInputValue);
    }, [titleInputValue, onEndEditingTitle]);

    const onKeyDown = React.useCallback(
        (evt: React.KeyboardEvent<unknown>) => {
            if (evt.which === KeyCodes.enter) {
                onConfirmTitle();
            }
        },
        [onConfirmTitle]
    );

    return (
        <div className={styles.calendarTitleContainer}>
            <TextField
                onChange={onChangeTitle}
                placeholder={titleInputValue || loc(calendarAssetName)}
                defaultValue={titleInputValue}
                borderless={!isEditing}
                styles={textFieldStyles}
                componentRef={titleTextFieldRef}
                onKeyDown={onKeyDown}
                onFocus={onStartEditTitle}
            />

            <div className={styles.calendarTitleButtonContainer}>
                {!isEditing ? (
                    <DefaultButton
                        onClick={onStartEditTitle}
                        className={styles.calendarTitleButton}
                        iconProps={editButtonIconProps}
                        title={loc(titleEditTooltip)}
                    />
                ) : (
                    <DefaultButton
                        onClick={onConfirmTitle}
                        className={styles.calendarTitleButton}
                        iconProps={confirmButtonIconProps}
                        title={loc(titleConfirmTooltip)}
                    />
                )}
            </div>
        </div>
    );
});

export function getTitleSection(titleProps: CalendarTitleProps): IContextualMenuItem {
    return {
        key: 'WidgetTitle',
        itemType: ContextualMenuItemType.Section,
        className: styles.titleSectionHeader,
        role: 'group',
        sectionProps: {
            title: loc(titleTextFieldLabel),
            items: [getTitleTextField(titleProps)],
        },
    };
}

function getTitleTextField(titleProps: CalendarTitleProps): IContextualMenuItem {
    return {
        key: 'Title',
        onRender: renderTitleTextField(titleProps),
    };
}

function renderTitleTextField(titleProps: CalendarTitleProps) {
    return (item: IContextualMenuItem) => {
        return <CalendarTitle title={titleProps.title} setTitle={titleProps.setTitle} />;
    };
}
